import type { PortableTextBlock, PortableTextTextBlock } from "sanity";
import { markNoLinksKeys, portableTextComponentKeys } from "src/lib/richtext/portableTextComponentKeys";
import _ from "lodash";

const MIN_PARAGRAPHS_BETWEEN_ADS = Number(process.env.NEXT_PUBLIC_MIN_PARAGRAPHS_BETWEEN_ADS ?? 0);

/**
 * Splits portable text into chunks so ads can be inserted between them.
 *
 * - After every 4 consecutive paragraphs.
 * - Before a heading, if 2+ consecutive paragraphs precede it (if an inline image sits directly
 *   before the heading, the break goes before the image instead).
 * - Heading-triggered breaks are suppressed unless at least MIN_PARAGRAPHS_BETWEEN_ADS paragraphs
 *   have passed since the last ad, so two ads can't land close together.
 *
 * A "paragraph" here is any countable block (see isCountableBlock) — headings and inline images
 * reset the streak; quotes/lists keep it alive without incrementing it.
 *
 * @param text The unmodified text
 * @returns The blocks split into chunks with ads or something between
 */
export function splitBlocksForAds(text: PortableTextBlock[]): PortableTextBlock[][] {
    //console.group("SPLIT BLOCKS");
    //console.debug("TEXT", JSON.stringify(text));
    const result: PortableTextBlock[][] = [];

    let paragraphCounter = 0;
    let paragraphCounterPrev = 0;
    let paragraphsSinceLastAd = 0;
    let lastChunkEndIndex = 0;
    let listItemCounter = 0;
    let prevBlockWasInlineImage = false;
    let blockIndex = 0; // defined outside for loop since I need it later
    for (; blockIndex < text.length; blockIndex++) {
        const block = text[blockIndex];
        const blockType = block._type;
        const styleType = (text[blockIndex].style ?? "normal") as string;
        //console.debug("Paracounter", paragraphCounter, block);
        if (isSkippableBlock(block)) {
            // count streamingProviders as part of the paragraph before it
        } else if (paragraphCounter === 4 && isCountableBlock(block)) {
            result.push(text.slice(lastChunkEndIndex, blockIndex));
            lastChunkEndIndex = blockIndex;
            paragraphCounterPrev = paragraphCounter;
            paragraphCounter = 0;
            paragraphsSinceLastAd = 0;
        } else if (paragraphCounter >= 2 && paragraphsSinceLastAd >= MIN_PARAGRAPHS_BETWEEN_ADS && /^h[1-6]$/i.test(styleType)) {
            if (blockIndex > 2) {// Hopp over annonse dersom denne headingen er for tidlig i artikkelen
                result.push(text.slice(lastChunkEndIndex, blockIndex));
                lastChunkEndIndex = blockIndex;
                paragraphsSinceLastAd = 0;
            }
            paragraphCounterPrev = paragraphCounter;
            paragraphCounter = 0;
        } else if (paragraphCounterPrev >= 2 && paragraphsSinceLastAd >= MIN_PARAGRAPHS_BETWEEN_ADS && prevBlockWasInlineImage && /^h[1-6]$/i.test(styleType)) {
            if (blockIndex > 2) {// Hopp over annonse dersom denne headingen er for tidlig i artikkelen
                //console.debug(lastChunkEndIndex, blockIndex, text[blockIndex]);
                result.push(text.slice(lastChunkEndIndex, blockIndex - 1));
                lastChunkEndIndex = blockIndex - 1;
                paragraphsSinceLastAd = 0;
            }
            paragraphCounterPrev = paragraphCounter;
            paragraphCounter = 0;
        }

        if (isCountableBlock(block)) {
            prevBlockWasInlineImage = false;
            // quotes and other countable blocks does not reset the counter, but quotes does not increment.
            // lists only increment after all list items are done
            if (block.listItem) {
                listItemCounter++;
            } else if (listItemCounter > 0) {
                listItemCounter = 0;
                paragraphCounterPrev = paragraphCounter;
                paragraphCounter++;
                paragraphsSinceLastAd++;
            }

            if (blockType !== "quote" && !block.listItem) {
                paragraphCounterPrev = paragraphCounter;
                paragraphCounter++;
                paragraphsSinceLastAd++;
            }
        } else {
            paragraphCounterPrev = paragraphCounter;
            paragraphCounter = 0;
            if (blockType === "inlineImage") {
                prevBlockWasInlineImage = true;
            } else {
                prevBlockWasInlineImage = false;
            }
        }
    }

    if (blockIndex !== lastChunkEndIndex) {
        result.push(text.slice(lastChunkEndIndex));
    }
    //console.groupEnd();
    return result;
}

function isCountableBlock(block: PortableTextBlock): boolean {
    if (/^h[1-6]$/i.test(block.style as string ?? "")) {
        return false; // headings should not be countable
    }
    if (COUNTABLE_TYPES.includes(block._type)) {
        return true;
    }
    return false;
}

function isSkippableBlock(block: PortableTextBlock): boolean {
    switch (block._type) {
        case "streamingProviders":
            return true;
    }
    if (block._type === "block") {
        // If the block only contains a single link, it should not be considered countable unless the block is a heading
        const pttb = (block as PortableTextTextBlock);
        if (pttb.style === "normal" && pttb.children?.length === 1 && pttb.markDefs?.some(md => markNoLinksKeys.includes(md._type))) {
            return true;
        }
    }

    return false;
}

const COUNTABLE_TYPES = ["block", ..._.without(portableTextComponentKeys, "inlineImage")];